<template>
  <div class="message-container">
    <el-card class="box-card">
      <template #header>
        <div class="clearfix">
          <span>匿名留言板（留下你珍贵的留言吧~）</span>
        </div>
      </template>

      <!-- 私密留言箱提示 -->
      <div v-if="matchedMessage && matchedMessage.isPublic === '0'" class="private-message">
        <el-alert
          title="私密留言箱"
          type="warning"
          :closable="false"
          show-icon>
          <p>该留言箱为私密状态，不支持查看和留言</p>
          <el-button type="text" @click="router.push('/')">返回首页</el-button>
        </el-alert>
      </div>
      
      <!-- 公开留言箱内容 -->
      <template v-else-if="matchedMessage">
        <!-- 匹配消息展示区 -->
        <div class="matched-message">
          <h3>{{ matchedMessage.title }}</h3>
          <p class="quote">主播寄语：{{ matchedMessage.content }}</p>
        </div>
        
        <!-- 表单区域 -->
        <el-form 
          ref="messageForm" 
          :model="message" 
          label-width="120px" 
          :rules="rules"
        >
          <el-form-item label="消息ID" style="display: none;">
            <el-input v-model="message.id" disabled></el-input>
          </el-form-item>
          
          <el-form-item label="标题" prop="title">
            <el-input 
              v-model="message.title" 
              placeholder="请输入留言标题"
              clearable
            ></el-input>
          </el-form-item>
          
          <el-form-item label="内容" prop="content">
            <el-input 
              type="textarea" 
              v-model="message.content" 
              :rows="4"
              placeholder="请输入留言内容"
              resize="vertical"
              show-word-limit
              maxlength="2000"
            ></el-input>
          </el-form-item>
          
          <el-form-item label="创建时间">
            <el-input v-model="message.createTime" disabled></el-input>
          </el-form-item>
        </el-form>
        
        <div class="button-group">
          <el-button @click="resetForm">重置</el-button>
          <el-button 
            type="primary" 
            @click="submitForm"
          >
            提交留言
          </el-button>
        </div>
      </template>
      
      <!-- 未找到提示 -->
      <div v-else class="not-found-message">
        <el-alert
          title="未能找到该留言箱"
          type="error"
          :closable="false"
          show-icon>
          <p>请确认链接是否正确，或联系管理员</p>
          <el-button type="text" @click="router.push('/')">返回首页</el-button>
        </el-alert>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElAlert } from 'element-plus'
import axios from 'axios'

const route = useRoute()
const router = useRouter()
const messageForm = ref(null)

// 表单数据
const message = reactive({
  id: '',
  title: '',
  content: '',
  createTime: ''
})

// 匹配到的消息
const matchedMessage = ref(null)

// 表单验证规则
const rules = reactive({
  title: [
    { required: true, message: '请输入标题', trigger: 'blur' }
  ],
  content: [
    { required: true, message: '请输入内容', trigger: 'blur' }
  ]
})

// 获取当前时间
const getCurrentTime = () => {
  const now = new Date()
  return now.toLocaleString('zh-CN', { 
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-')
}

// 加载匹配消息
const loadMatchedMessage = async (id) => {
  try {
    // 只按 messageId 查这一条：后端对非管理员已收窄为「必须带 messageId」，
    // 不再把全部留言箱（含他人私密箱）一次性下发到浏览器
    const response = await axios.get('/adminapi/message/list', { params: { messageId: id } })
    const records = (response.data && response.data.data && response.data.data.records) || []
    matchedMessage.value = records.find(
      item => item.messageId === id
    ) || null
    
    if (!matchedMessage.value) {
      ElMessage.error('未找到匹配的留言箱')
      resetForm()
    }
  } catch (error) {
    console.error('加载消息失败:', error)
    ElMessage.error('加载匹配消息失败')
    router.push('/')
  }
}

// 表单提交
const submitForm = () => {
  messageForm.value.validate(async (valid) => {
    if (valid) {
      try {
        message.createTime = getCurrentTime()
        const response = await axios.post('/adminapi/words/add', message)
        
        if (response.data.code === 200) {
          ElMessage.success('感谢你的留言~')
          resetForm()
        } else {
          ElMessage.error(response.data.message || '提交失败')
        }
      } catch (error) {
        console.error('提交错误:', error)
        ElMessage.error('网络错误，请重试')
      }
    }
  })
}

// 重置表单
const resetForm = () => {
  messageForm.value.resetFields()
  message.createTime = getCurrentTime()
}

// 初始化
onMounted(() => {
  try {
    const idMatch = route.path.match(/msg_(\d+)/)
    if (idMatch?.[1]) {
      message.id = `msg_${idMatch[1]}`
      message.createTime = getCurrentTime()
      loadMatchedMessage(message.id)
    } else {
      ElMessage.error('无效的消息ID')
      router.push('/')
    }
  } catch (error) {
    console.error('初始化错误:', error)
    router.push('/')
  }
})
</script>

<style scoped>
/* 基础设置 */
html {
  font-size: 16px;
}

.message-container {
  padding: 1.25rem;
  max-width: 56.25rem;
  margin: 0 auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.box-card {
  margin-top: 1.25rem;
  border-radius: 0.75rem;
  box-shadow: 0 0.375rem 1.125rem rgba(0, 0, 0, 0.08);
  border: none;
  flex: 1;
  display: flex;
  flex-direction: column;
}

:deep(.el-card__header) {
  background: linear-gradient(90deg, #409eff 0%, #67c23a 100%);
  color: white;
  border-radius: 0.75rem 0.75rem 0 0;
  font-weight: bold;
  padding: 1rem;
}

.matched-message {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 0.5rem;
  border-left: 0.25rem solid #409eff;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.05);
}

.matched-message h3 {
  margin: 0 0 0.75rem 0;
  color: #303133;
  font-size: 1.125rem;
  font-weight: 600;
}

.quote {
  position: relative;
  padding-left: 1.25rem;
  color: #606266;
  line-height: 1.6;
}

.quote::before {
  content: "“";
  position: absolute;
  left: 0;
  top: -0.3125rem;
  font-size: 1.5rem;
  color: #409eff;
  opacity: 0.6;
}

.private-message,
.not-found-message {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  border-radius: 0.5rem;
}

.private-message {
  background: rgba(255, 244, 235, 0.9);
  border-left: 0.25rem solid #e6a23c;
}

.not-found-message {
  background: rgba(255, 235, 235, 0.9);
  border-left: 0.25rem solid #f56c6c;
}

.private-message p,
.not-found-message p {
  margin: 0.625rem 0 0;
  color: #606266;
}

.button-group {
  text-align: right;
  margin-top: 1.875rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
}

.el-button {
  border-radius: 0.375rem;
  padding: 0.625rem 1.25rem;
  font-weight: 500;
  transition: all 0.3s;
  min-height: 2.75rem;
}

.el-button:hover {
  opacity: 0.9;
  transform: translateY(-0.125rem);
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.1);
}

/* 移动端适配 */
@media (max-width: 768px) {
  html {
    font-size: 14px;
  }
  
  .message-container {
    padding: 0.9375rem;
  }
  
  :deep(.el-form-item__label) {
    text-align: left;
    margin-bottom: 0.5rem;
  }
  
  :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
  
  :deep(.el-input),
  :deep(.el-textarea) {
    width: 100%;
  }
  
  .el-button {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
  
  :deep(.el-card__header) {
    padding: 0.75rem;
  }
}

/* 触摸设备优化 */
@media (hover: none) {
  .el-button:hover {
    opacity: 1;
    transform: none;
    box-shadow: none;
  }
}
</style>